import {
  ReactElement,
  MouseEvent,
  ChangeEvent,
  useMemo,
  useCallback,
} from 'react';
import {
  Typography,
  Stack,
  Button,
  FormControlLabel,
  Collapse,
  Tooltip,
} from '@mui/material';
import {
  ChangeCircle as ChangeStateIcon,
  ManageAccounts as ModifyAssignmentIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  AddLocation as AddRouteToRouteIcon,
} from '@mui/icons-material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFileArrowDown as ExportListIcon,
  faBarcode as BardCodeIcon,
  faClone as DuplicateIcon,
  faRoad as GenerateRouteIcon,
  faMap as MapIcon,
  faChevronDown as KeyboardArrowDownIcon,
  faArrowsRotate as RefreshIcon,
} from '@fortawesome/free-solid-svg-icons';
import { useSnackbar } from 'notistack';
import moment from 'moment';

import {
  useAppSelector,
  useLazyRequest,
  useExportTickets,
  useExportTicketsMl,
  useToggle,
  useToggleMenu,
  useToggleDialogConfirmation,
  useSubscriptionFeatures,
} from '~hooks/index';
import { selectAuth } from '~store/slices/auth/index';
import { isOnlyReadUser, isOnlyCreatorOrder } from '~utils/user';
import { ApiTransactionRoutes } from '~services/apiTransaction/types';
import {
  searchItemsToExportV2,
  duplicateItems,
  deleteItems,
} from '~services/item';
import { addRouteItemsInRoute } from '~services/route';
import {
  ActionOrderMenu,
  AccessFeature,
  AddOrdesToRouteMenu,
  DialogChangeOrderState,
  DialogModifyAssignmentOrders,
  DialogOrdersMultipeEdition,
  DialogConfimation,
  AssistantGenerateRoutes,
} from '~components/index';
import { ActionOrderMenuOptionItem } from '~components/ActionOrderMenu/types';
import { Nullable } from '~globals/types/commons';
import { PlanFeaturesTypes } from '~globals/types/enums';
import { getFeatureAvailableToDate } from '~utils/feature';
import { formatterDate } from '~utils/formatter';
import { downloadFileByRequest } from '~utils/download';

import { useDashboardVariantV2CarrierContext } from '../../../../../Context';
// eslint-disable-next-line max-len
import { useDashboardVariantV2CarrierInWarehouseFiltersContext } from '../../../../../InWarehouseFiltersContext';
import { useDashboardVariantV2CarrierPresetsContext } from '../../../../../PresetsContext';
import { useDashboardVariantV2CarrierContentInWarehouseTabContext } from '../../Context';

import {
  DashboardVariantV2CarrierContentActionsContainer,
  DashboardVariantV2CarrierContentAccessFeatureButtonSwitch,
  DashboardVariantV2CarrierContentButtonSwitchInput,
  DashboardVariantV2CarrierContentFormControlWithBackground,
  DashboardVariantV2CarrierContentFormControlCheckBox,
  DashboardVariantV2CarrierContentActionsIconButton,
  DashboardVariantV2CarrierContentActionsIconButtonIcon,
} from '../../../../styles';

const DashboardVariantV2CarrierContentInWarehouseTabActions =
  (): ReactElement => {
    const { enqueueSnackbar } = useSnackbar();
    const { user } = useAppSelector(selectAuth);
    const { hasAccessToFeature } = useSubscriptionFeatures();

    const isUserOnlyReader = useMemo(() => isOnlyReadUser(user), [user]);
    const isUserOnlyCreatorOrder = useMemo(
      () => isOnlyCreatorOrder(user),
      [user],
    );

    const {
      data: {
        statistics: {
          data: {
            companyInfo: { quantityGiverCompanies },
          },
        },
        zoneSaturation: { reload: reloadZoneSaturation },
        reloadDataByConfig: reloadMainDataByConfig,
      },
      ignoreRefreshBySocket: { toggleValue: toggleIgnoreRefreshBySocket },
      expandedHasChangeOrdersAlert: {
        toggleValue: toggleExpandedHasChangeOrdersAlert,
      },
      modifiedItemsIdBySocket: { setValue: setModifiedItemsIdBySocket },
    } = useDashboardVariantV2CarrierContext();

    const {
      filters: { handleClearFilters, handleClearFilterByValues },
      forceRequest: { toggleValue: toggleForceRequest },
    } = useDashboardVariantV2CarrierInWarehouseFiltersContext();

    const {
      selectedPreset: { reset: handleResetSelectedPreset },
    } = useDashboardVariantV2CarrierPresetsContext();

    const {
      selection: { selectedIds, utils: selectedIdsUtils },
      parseFiltersRequest,
      table: {
        queryParams: { pagination },
        reloadRequest: reloadRequestTable,
      },
      map: {
        showMap: { open: showMap, toggleOpen: toogleShowMap },
        data: {
          warehouses: { reloadRequest: reloadMapWarehouses },
        },
      },
      actions: { reloadDataAndResetSelectedRows },
      isOnlyTodayDelivery: {
        value: isOnlyTodayDelivery,
        toggleValue: toggleIsOnlyTodayDelivery,
      },
      includeCollectedItems: {
        value: includeCollectedItems,
        toggleValue: toggleIncludeCollectedItems,
      },
    } = useDashboardVariantV2CarrierContentInWarehouseTabContext();

    const isValidRoleUser = useMemo(
      () => !isUserOnlyReader || !isUserOnlyCreatorOrder,
      [isUserOnlyCreatorOrder, isUserOnlyReader],
    );

    const isEmptySelectedIds = useMemo(
      () => selectedIdsUtils.isEmpty(),
      [selectedIdsUtils],
    );

    const isEnabledIncludeCollectedItems = useMemo(() => {
      if (
        isUserOnlyCreatorOrder ||
        !hasAccessToFeature(
          PlanFeaturesTypes.MgmGiversModule,
          getFeatureAvailableToDate(PlanFeaturesTypes.MgmGiversModule),
        )
      ) {
        return false;
      }

      return quantityGiverCompanies > 0;
    }, [hasAccessToFeature, isUserOnlyCreatorOrder, quantityGiverCompanies]);

    const handleIgnoreRefreshBySocket = useCallback(() => {
      toggleIgnoreRefreshBySocket(true);
    }, [toggleIgnoreRefreshBySocket]);

    const handleChangeIncludeCollectedItems = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        const { checked } = event.target ?? event.currentTarget;
        // The tab context reloads the list + map on this toggle change.
        toggleIncludeCollectedItems(checked);
      },
      [toggleIncludeCollectedItems],
    );

    const handleChangeIsTodayDelivery = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        const { checked } = event.target ?? event.currentTarget;
        toggleIsOnlyTodayDelivery(checked);

        let expirationDateTimeFrom: Nullable<Date> = null;
        let expirationDateTimeTo: Nullable<Date> = null;

        if (checked) {
          const currentDate = moment();
          expirationDateTimeFrom = currentDate.startOf('day').toDate();
          expirationDateTimeTo = currentDate.clone().endOf('day').toDate();
        }

        handleClearFilters(false);
        handleClearFilterByValues(
          { expirationDateTimeFrom, expirationDateTimeTo },
          () => toggleForceRequest(true),
        );
        reloadZoneSaturation({ withLoading: true, onlyTodayDelivery: checked });
        handleResetSelectedPreset();
      },
      [
        handleClearFilterByValues,
        handleClearFilters,
        handleResetSelectedPreset,
        toggleForceRequest,
        toggleIsOnlyTodayDelivery,
        reloadZoneSaturation,
      ],
    );

    const [, loadingSearchToExport, , executeSearchToExport] = useLazyRequest({
      request: searchItemsToExportV2,
      withPostSuccess: (response) => {
        if (response.headers && response.data) {
          const currentDate = formatterDate(new Date(), { format: 'YYYYMMDD' });

          const defaultFileName = `pedidos-en-deposito-${currentDate}.csv`;

          downloadFileByRequest(
            response.headers,
            response.data,
            defaultFileName,
          );

          enqueueSnackbar(`Se ha exportado los pedidos correctamente`, {
            variant: 'success',
          });
        } else {
          enqueueSnackbar(
            'No se pudo exportar los pedidos, intente nuevamente',
            { variant: 'error' },
          );
        }
      },
      withPostFailure: () => {
        enqueueSnackbar('No se pudo exportar los pedidos, intente nuevamente', {
          variant: 'error',
        });
      },
    });

    const handleExportItems = useCallback(async () => {
      await executeSearchToExport({
        filters: parseFiltersRequest(),
        pagination,
      });
    }, [executeSearchToExport, pagination, parseFiltersRequest]);

    const { loading: loadingExportTickets, onExportTickets } =
      useExportTickets();

    const handleExportTickets = useCallback(async () => {
      if (!isEmptySelectedIds) {
        await onExportTickets({
          itemIds: selectedIds,
        });
      }
    }, [isEmptySelectedIds, selectedIds, onExportTickets]);

    const { loading: loadingExportTicketsMl, onExportTicketsMl } =
      useExportTicketsMl();

    const handleExportTicketsMl = useCallback(async () => {
      if (!isEmptySelectedIds) {
        await onExportTicketsMl(selectedIds);
      }
    }, [isEmptySelectedIds, onExportTicketsMl, selectedIds]);

    const [openDialogChangeStateOrders, toggleOpenDialogChangeStateOrders] =
      useToggle(false);

    const [
      openDialogModifyAssignmentOrders,
      toggleDialogModifyAssignmentOrders,
    ] = useToggle(false);

    const [openDialogMultipleEdition, toggleDialogMultipleEdition] =
      useToggle(false);

    const [openConfirmDuplicateOrders, toggleOpenConfirmDuplicateOrders] =
      useToggle(false);

    const [openConfirmDeleteOrders, toggleOpenConfirmDeleteOrders] =
      useToggle(false);

    const actionsWitSelectionMenuOptions = useMemo<ActionOrderMenuOptionItem[]>(
      () => [
        {
          label: 'Cambiar estado de pedidos',
          Icon: <ChangeStateIcon color="success" />,
          enabled: !isUserOnlyReader,
          onClick: () => toggleOpenDialogChangeStateOrders(true),
        },
        {
          label: 'Modificar asignación de pedidos',
          Icon: <ModifyAssignmentIcon color="success" />,
          enabled: !isUserOnlyReader,
          onClick: () => toggleDialogModifyAssignmentOrders(true),
          validSubscriptionFeature: {
            validFeature: PlanFeaturesTypes.LimitNotPro,
          },
        },
        {
          label: 'Exportar etiquetas',
          Icon: <FontAwesomeIcon icon={BardCodeIcon} />,
          loading: loadingExportTickets,
          enabled: true,
          onClick: handleExportTickets,
          validSubscriptionFeature: {
            validFeature: PlanFeaturesTypes.MgmGenerateTickets,
            availableToDate: getFeatureAvailableToDate(
              PlanFeaturesTypes.MgmGenerateTickets,
            ),
          },
        },
        {
          label: 'Exportar etiquetas de Mercado Libre',
          Icon: <FontAwesomeIcon icon={BardCodeIcon} />,
          loading: loadingExportTicketsMl,
          enabled: !isUserOnlyReader,
          onClick: handleExportTicketsMl,
          validSubscriptionFeature: {
            validFeature: PlanFeaturesTypes.MgmGenerateTickets,
            availableToDate: getFeatureAvailableToDate(
              PlanFeaturesTypes.MgmGenerateTickets,
            ),
          },
        },
        {
          label: 'Edición múltiple',
          Icon: <EditIcon color="success" />,
          enabled: !isUserOnlyReader,
          onClick: () => toggleDialogMultipleEdition(true),
          validSubscriptionFeature: {
            validFeature: PlanFeaturesTypes.LimitNotPro,
          },
          newActiveForVersion: '1.18.6',
        },
        {
          label: 'Repetir destinos',
          Icon: <FontAwesomeIcon icon={DuplicateIcon} />,
          enabled: !isUserOnlyReader,
          onClick: () => toggleOpenConfirmDuplicateOrders(true),
        },
        {
          label: 'Eliminar pedidos',
          Icon: <DeleteIcon color="error" />,
          enabled: !isUserOnlyReader,
          onClick: () => toggleOpenConfirmDeleteOrders(true),
        },
      ],
      [
        isUserOnlyReader,
        loadingExportTickets,
        handleExportTickets,
        loadingExportTicketsMl,
        handleExportTicketsMl,
        toggleDialogMultipleEdition,
        toggleOpenConfirmDuplicateOrders,
        toggleOpenConfirmDeleteOrders,
        toggleOpenDialogChangeStateOrders,
        toggleDialogModifyAssignmentOrders,
      ],
    );

    const actionsGeneralMenuOptions = useMemo<ActionOrderMenuOptionItem[]>(
      () => [
        {
          label: 'Exportar listado',
          Icon: <FontAwesomeIcon icon={ExportListIcon} />,
          loading: loadingSearchToExport,
          enabled: true,
          disabled: false,
          onClick: handleExportItems,
        },
      ],
      [loadingSearchToExport, handleExportItems],
    );

    const [
      anchorElMenuAddOrdersToRoute,
      openMenuAddOrdersToRoute,
      ,
      handleToggleMenuAddOrdersToRoute,
    ] = useToggleMenu();

    const [
      openConfirmAddOrders,
      selectedRouteAddOrders,
      handleOpenConfirmAddOrders,
      handleCloseConfirmAddOrders,
    ] = useToggleDialogConfirmation<Nullable<ApiTransactionRoutes>>(null);

    const onClickCardAddOrderToRouteMenu = useCallback(
      (route: ApiTransactionRoutes) => {
        handleToggleMenuAddOrdersToRoute(false);
        handleOpenConfirmAddOrders(route);
      },
      [handleToggleMenuAddOrdersToRoute, handleOpenConfirmAddOrders],
    );

    const [
      anchorElDialogAssistantGenerateRoutesMenuRef,
      openDialogAssistantGenerateRoutes,
      ,
      handleToggleDialogAssistantGenerateRoutes,
    ] = useToggleMenu();

    const [
      openDialogAssistantGenerateRoutesByItems,
      toggleOpenDialogAssistantGenerateRoutesByItems,
    ] = useToggle(false);

    const [
      openDialogAssistantGenerateRoutesByTemplate,
      toggleOpenDialogAssistantGenerateRoutesByTemplate,
    ] = useToggle(false);

    const toggleViewMap = useCallback(
      (event: MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        toogleShowMap();

        selectedIdsUtils.reset();

        reloadRequestTable();
      },
      [reloadRequestTable, selectedIdsUtils, toogleShowMap],
    );

    const handleSuccessChangeOrdersState = useCallback(() => {
      handleIgnoreRefreshBySocket();

      toggleOpenDialogChangeStateOrders(false);

      reloadDataAndResetSelectedRows();
    }, [
      reloadDataAndResetSelectedRows,
      handleIgnoreRefreshBySocket,
      toggleOpenDialogChangeStateOrders,
    ]);

    const [, loadingDuplicateOrders, , executeDuplicateOrders] = useLazyRequest(
      {
        request: duplicateItems,
        withPostSuccess: () => {
          enqueueSnackbar('Destinos repetidos correctamente', {
            variant: 'success',
          });

          toggleOpenConfirmDuplicateOrders();

          reloadDataAndResetSelectedRows();
        },
        withPostFailure: () => {
          enqueueSnackbar(
            'No se pudo repetir los destinos, intente nuevamente',
            { variant: 'error' },
          );
        },
      },
    );

    const handleDuplicateOrders = useCallback(() => {
      if (!isEmptySelectedIds) {
        executeDuplicateOrders(selectedIds);
      }
    }, [executeDuplicateOrders, isEmptySelectedIds, selectedIds]);

    const [, loadingDeleteOrders, , executeDeleteOrders] = useLazyRequest({
      request: deleteItems,
      withPostSuccess: () => {
        enqueueSnackbar('Pedidos eliminados correctamente', {
          variant: 'success',
        });

        handleIgnoreRefreshBySocket();

        toggleOpenConfirmDeleteOrders(false);

        reloadDataAndResetSelectedRows();
      },
      withPostFailure: () => {
        enqueueSnackbar('No se pudo elimnar los pedidos, intente nuevamente', {
          variant: 'error',
        });
      },
    });

    const handleDeleteOrders = useCallback(async () => {
      if (!isEmptySelectedIds) {
        await executeDeleteOrders(selectedIds);
      }
    }, [executeDeleteOrders, isEmptySelectedIds, selectedIds]);

    const messageDialogConfimationAddOrders = useMemo(
      () =>
        // eslint-disable-next-line max-len
        `Los pedidos seleccionados serán agregados a la ruta #${selectedRouteAddOrders?.routeCode.toUpperCase()}`,
      [selectedRouteAddOrders],
    );

    const [, loadingAddOrdersToRoute, , executeAddOrdersToRoute] =
      useLazyRequest({
        request: addRouteItemsInRoute,
        withPostSuccess: () => {
          handleIgnoreRefreshBySocket();

          reloadDataAndResetSelectedRows();

          enqueueSnackbar(
            // eslint-disable-next-line max-len
            `Las paradas fueron agregadas correctamente a la ruta #${selectedRouteAddOrders?.routeCode.toUpperCase()}`,
            { variant: 'success' },
          );

          handleCloseConfirmAddOrders();
        },
        withPostFailure: () => {
          enqueueSnackbar(
            'No se pudo agregar las paradas de la ruta, intente nuevamente',
            { variant: 'error' },
          );
        },
      });

    const handleAddOrdersToRoute = useCallback(async () => {
      if (selectedRouteAddOrders) {
        await executeAddOrdersToRoute({
          routeId: selectedRouteAddOrders.routeId,
          itemIds: selectedIds,
        });
      }
    }, [executeAddOrdersToRoute, selectedRouteAddOrders, selectedIds]);

    const handleRefreshData = useCallback(() => {
      reloadDataAndResetSelectedRows();
      reloadMainDataByConfig(false);
      reloadZoneSaturation({
        withLoading: true,
        onlyTodayDelivery: isOnlyTodayDelivery,
      });

      reloadMapWarehouses();

      toggleExpandedHasChangeOrdersAlert(false);
      toggleIgnoreRefreshBySocket(false);
      setModifiedItemsIdBySocket([]);
    }, [
      reloadMainDataByConfig,
      reloadDataAndResetSelectedRows,
      reloadZoneSaturation,
      isOnlyTodayDelivery,
      reloadMapWarehouses,
      toggleExpandedHasChangeOrdersAlert,
      toggleIgnoreRefreshBySocket,
      setModifiedItemsIdBySocket,
    ]);

    return (
      <DashboardVariantV2CarrierContentActionsContainer justifyContent="space-between">
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography component="h6" variant="h6" fontWeight="bold">
            PEDIDOS EN DEPÓSITO
          </Typography>

          <Tooltip title="Refrescar listado" arrow>
            <DashboardVariantV2CarrierContentActionsIconButton
              color="secondary"
              variant="outlined"
              onClick={handleRefreshData}
            >
              <DashboardVariantV2CarrierContentActionsIconButtonIcon
                icon={RefreshIcon}
              />
            </DashboardVariantV2CarrierContentActionsIconButton>
          </Tooltip>

          {!isUserOnlyReader && (
            <Collapse
              in={!isEmptySelectedIds}
              timeout="auto"
              unmountOnExit
              orientation="horizontal"
            >
              <ActionOrderMenu
                options={actionsWitSelectionMenuOptions}
                buttonProps={{ label: 'Selección', sx: { minHeight: 40 } }}
              />
            </Collapse>
          )}
        </Stack>

        <Stack direction="row" spacing={2}>
          {isValidRoleUser && (
            <>
              <AccessFeature.Button
                validFeature={PlanFeaturesTypes.LimitNotPro}
                variant="contained"
                color="secondary"
                startIcon={<AddRouteToRouteIcon />}
                ref={anchorElMenuAddOrdersToRoute}
                onClick={() => handleToggleMenuAddOrdersToRoute(true)}
                disabled={isEmptySelectedIds}
              >
                Agregar a ruta
              </AccessFeature.Button>

              {openMenuAddOrdersToRoute && (
                <AddOrdesToRouteMenu
                  id="active-routes-menu"
                  anchorEl={anchorElMenuAddOrdersToRoute.current}
                  open={openMenuAddOrdersToRoute}
                  onClose={() => handleToggleMenuAddOrdersToRoute(false)}
                  arrow
                  placement="bottom"
                  onClickCard={onClickCardAddOrderToRouteMenu}
                />
              )}
            </>
          )}

          {isValidRoleUser && (
            <>
              <Button
                variant="contained"
                color="primary"
                startIcon={<FontAwesomeIcon icon={GenerateRouteIcon} />}
                endIcon={<FontAwesomeIcon icon={KeyboardArrowDownIcon} />}
                ref={anchorElDialogAssistantGenerateRoutesMenuRef}
                onClick={() => handleToggleDialogAssistantGenerateRoutes(true)}
                // disabled={isEmptySelectedIds}
              >
                Generar rutas
              </Button>

              <AssistantGenerateRoutes.ActionMenu
                anchorEl={anchorElDialogAssistantGenerateRoutesMenuRef.current}
                open={openDialogAssistantGenerateRoutes}
                onClose={() => handleToggleDialogAssistantGenerateRoutes(false)}
                bytItems={{
                  disabled: isEmptySelectedIds,
                  onOpenDialog: () =>
                    toggleOpenDialogAssistantGenerateRoutesByItems(true),
                }}
                byTemplate={{
                  onOpenDialog: () =>
                    toggleOpenDialogAssistantGenerateRoutesByTemplate(true),
                }}
              />
            </>
          )}

          <DashboardVariantV2CarrierContentFormControlWithBackground
            control={
              <DashboardVariantV2CarrierContentFormControlCheckBox
                checked={isOnlyTodayDelivery}
                onChange={handleChangeIsTodayDelivery}
              />
            }
            label="Solo envíos que vencen hoy"
          />

          {isEnabledIncludeCollectedItems && (
            <DashboardVariantV2CarrierContentFormControlWithBackground
              control={
                <DashboardVariantV2CarrierContentFormControlCheckBox
                  checked={includeCollectedItems}
                  onChange={handleChangeIncludeCollectedItems}
                />
              }
              label="Incluir pedidos colectados"
            />
          )}

          {!isUserOnlyReader && (
            <ActionOrderMenu options={actionsGeneralMenuOptions} />
          )}

          <DashboardVariantV2CarrierContentAccessFeatureButtonSwitch
            validFeature={PlanFeaturesTypes.MgmItemMap}
            availableToDate={getFeatureAvailableToDate(
              PlanFeaturesTypes.MgmItemMap,
            )}
            variant="outlined"
            color="primary"
            startIcon={<FontAwesomeIcon icon={MapIcon} />}
            onClick={toggleViewMap}
            disableRipple
            disableFocusRipple
            disableTouchRipple
            sx={{ borderColor: 'transparent' }}
          >
            <FormControlLabel
              control={
                <DashboardVariantV2CarrierContentButtonSwitchInput
                  checked={showMap}
                />
              }
              label="Ver Mapa"
              labelPlacement="start"
              componentsProps={{
                typography: {
                  variant: 'inherit',
                },
              }}
              sx={{ marginLeft: 0 }}
            />
          </DashboardVariantV2CarrierContentAccessFeatureButtonSwitch>
        </Stack>

        <DialogChangeOrderState
          open={openDialogChangeStateOrders}
          onClose={() => toggleOpenDialogChangeStateOrders(false)}
          itemsIds={selectedIds}
          onChangeSuccess={handleSuccessChangeOrdersState}
        />

        {openDialogModifyAssignmentOrders && (
          <DialogModifyAssignmentOrders
            open={openDialogModifyAssignmentOrders}
            onClose={() => toggleDialogModifyAssignmentOrders(false)}
            itemIds={selectedIds}
            onSuccessModify={() => reloadDataAndResetSelectedRows()}
          />
        )}

        {openDialogMultipleEdition && (
          <DialogOrdersMultipeEdition
            open={openDialogMultipleEdition}
            onClose={() => toggleDialogMultipleEdition(false)}
            selectedIds={selectedIds}
            onSuccessSubmit={reloadDataAndResetSelectedRows}
          />
        )}

        <DialogConfimation
          open={openConfirmDuplicateOrders}
          description="Los destinos seleccionados serán repetidos"
          onConfirm={handleDuplicateOrders}
          onCancel={() => toggleOpenConfirmDuplicateOrders(false)}
          loadingConfirm={loadingDuplicateOrders}
        />

        <DialogConfimation
          open={openConfirmDeleteOrders}
          description="Los pedidos seleccionados serán eliminados"
          onConfirm={handleDeleteOrders}
          onCancel={() => toggleOpenConfirmDeleteOrders(false)}
          loadingConfirm={loadingDeleteOrders}
        />

        <DialogConfimation
          open={openConfirmAddOrders}
          description={messageDialogConfimationAddOrders}
          onConfirm={handleAddOrdersToRoute}
          onCancel={handleCloseConfirmAddOrders}
          loadingConfirm={loadingAddOrdersToRoute}
        />

        {openDialogAssistantGenerateRoutesByItems && (
          <AssistantGenerateRoutes.Dialog.ByItems
            open={openDialogAssistantGenerateRoutesByItems}
            onClose={() =>
              toggleOpenDialogAssistantGenerateRoutesByItems(false)
            }
            itemIds={selectedIds}
            actions={{
              onGenerateRouteComplete: handleIgnoreRefreshBySocket,
              onStepperComplete: () => reloadDataAndResetSelectedRows(),
            }}
          />
        )}

        {openDialogAssistantGenerateRoutesByTemplate && (
          <AssistantGenerateRoutes.Dialog.ByTemplate
            open={openDialogAssistantGenerateRoutesByTemplate}
            onClose={() =>
              toggleOpenDialogAssistantGenerateRoutesByTemplate(false)
            }
          />
        )}
      </DashboardVariantV2CarrierContentActionsContainer>
    );
  };

export default DashboardVariantV2CarrierContentInWarehouseTabActions;
